import type { HttpLayer } from '../http.js';
import type {
  CreateLocalWorkspaceRequest,
  RestoreVersionRequest,
  SaveCheckpointRequest,
  VersionHistoryResponse,
  VersioningOpResponse,
  ViewHistoryParams,
  WorkspaceProjection,
  WorkspaceNameEntry,
  WorkspaceRef,
  WorkspaceOperationHistory,
  SubscribeWorkspaceEventsRequest,
  CreateCloudWorkspaceRequest,
  BindWorkspaceRequest,
  ReassignWorkstationRequest,
  UnbindWorkspaceRequest,
  ConvertWorkspaceRequest,
  ArchiveWorkspaceRequest,
  WorkspaceLifecycleActionRequest,
  RetryWorkspaceArchiveCleanupRequest,
  CancelWorkspaceOperationRequest,
  CloudRepository,
  ExecuteFileOpRequest,
  FileOpResponse,
  FileReadResult,
  FileWriteResult,
  PodInfo,
  RepositoryStatusResponse,
  TransferWorkspaceRequest,
  SaveWorkspaceEnvironmentRequest,
} from '../types/index.js';
import {
  parseWorkspaceNameEntryList,
  parseWorkspaceProjection,
  parseWorkspaceProjectionList,
} from '../workspace-projection.js';

const cloudFilesystemPrefix = '/v1/cloud-filesystem';

export class FileWriteConflictError extends Error {
  constructor(
    public readonly path: string,
    public readonly currentVersion: string | null,
    public readonly currentContent?: string
  ) {
    super(`writeFile(${path}) conflict: file changed since it was read`);
    this.name = 'FileWriteConflictError';
  }
}

export function parseFileReadResponse(path: string, response: FileOpResponse): FileReadResult {
  if (response.status === 'error' || response.error) {
    throw new Error(`readFile(${path}) failed: ${response.error ?? 'unknown error'}`);
  }
  if (response.status !== 'ok' && response.status !== 'success') {
    throw new Error(`readFile(${path}) returned unknown status: ${response.status}`);
  }
  if (typeof response.content !== 'string') {
    throw new Error(`readFile(${path}) returned no content`);
  }
  if (response.encoding !== 'utf8' && response.encoding !== 'base64') {
    throw new Error(`readFile(${path}) returned unknown encoding: ${String(response.encoding)}`);
  }
  return {
    content: response.content,
    encoding: response.encoding,
    version: response.version,
  };
}

export class WorkspacesResource {
  constructor(private readonly http: HttpLayer) {}

  async list(deviceId?: string): Promise<WorkspaceProjection[]> {
    return parseWorkspaceProjectionList(
      await this.http.get('/v1/workspaces', { device_id: deviceId })
    );
  }

  /** Complete id/name catalog for every non-deleted workspace, equipment-free (workspace_state ADR-6adfe). */
  async listNames(): Promise<WorkspaceNameEntry[]> {
    return parseWorkspaceNameEntryList(await this.http.get('/v1/workspace-names'));
  }

  async getState(workspace: WorkspaceRef, deviceId?: string): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.get(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/state`, { device_id: deviceId })
    );
  }

  async getStateByName(name: string, deviceId?: string): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.get(`/v1/workspaces/by-name/${encodeURIComponent(name)}`, {
        device_id: deviceId,
      })
    );
  }

  /**
   * Resolve a caller-supplied name reference to the canonical workspace id via
   * the tenant-guarded by-name route (workspace_state ADR-6adfe); a bare id —
   * the internal key held by ChatSession and projections — passes through
   * unchanged. No guessing: an unknown or ambiguous name surfaces the
   * backend 404/409 verbatim.
   */
  private async resolveWorkspaceId(workspace: WorkspaceRef): Promise<string> {
    if (typeof workspace === 'string') return workspace;
    const projection = await this.getStateByName(workspace.name);
    return projection.workspace_id;
  }

  async createLocal(params: CreateLocalWorkspaceRequest): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(await this.http.post('/v1/workspaces/local', params));
  }

  /** Create a cloud workspace, optionally publishing a selected connected-device folder first. */
  async create(params: CreateCloudWorkspaceRequest): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(await this.http.post('/v1/workspaces', params));
  }

  async bind(workspace: WorkspaceRef, params: BindWorkspaceRequest): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/bindings`, params)
    );
  }

  /**
   * Move a local workspace onto a different computer. Unlike {@link bind} this
   * does not require the OUTGOING device to be connected — the premise is that
   * it is gone.
   */
  async reassignWorkstation(
    workspace: WorkspaceRef,
    params: ReassignWorkstationRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/actions/reassign-workstation`, params)
    );
  }

  async unbind(
    workspace: WorkspaceRef,
    deviceId: string,
    params: UnbindWorkspaceRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(
        `/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/bindings/${deviceId}/actions/unbind`,
        params
      )
    );
  }

  async convert(
    workspace: WorkspaceRef,
    params: ConvertWorkspaceRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/actions/convert`, params)
    );
  }

  async archive(
    workspace: WorkspaceRef,
    params: ArchiveWorkspaceRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/actions/archive`, params)
    );
  }

  async wake(
    workspace: WorkspaceRef,
    params: WorkspaceLifecycleActionRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/actions/wake`, params)
    );
  }

  async sleep(
    workspace: WorkspaceRef,
    params: WorkspaceLifecycleActionRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/actions/sleep`, params)
    );
  }

  async retryArchiveCleanup(
    workspace: WorkspaceRef,
    params: RetryWorkspaceArchiveCleanupRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/actions/retry-cleanup`, params)
    );
  }

  /** Cancel a running cloud setup, transfer, or pre-swap conversion owned by the caller or administered by an account owner. */
  async cancelOperation(
    workspace: WorkspaceRef,
    operationId: string,
    params: CancelWorkspaceOperationRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(
        `/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/operations/${operationId}/actions/cancel`,
        params
      )
    );
  }

  async transfer(
    workspace: WorkspaceRef,
    params: TransferWorkspaceRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/actions/transfer`, params)
    );
  }

  async saveEnvironment(
    workspace: WorkspaceRef,
    params: SaveWorkspaceEnvironmentRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/environment`, params)
    );
  }

  async getOperationHistory(
    workspace: WorkspaceRef,
    limit: number = 20
  ): Promise<WorkspaceOperationHistory> {
    return this.http.get(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/state/history`, { limit });
  }

  async listRepositories(): Promise<CloudRepository[]> {
    return this.http.get(`${cloudFilesystemPrefix}/repositories`);
  }

  async getRepositoryStatus(workspace: WorkspaceRef): Promise<RepositoryStatusResponse> {
    return this.http.get(`${cloudFilesystemPrefix}/repositories/${await this.resolveWorkspaceId(workspace)}`);
  }

  async listSandboxes(workspace: WorkspaceRef): Promise<PodInfo[]> {
    return this.http.get(`${cloudFilesystemPrefix}/sandboxes/${await this.resolveWorkspaceId(workspace)}`);
  }

  async executeFileOp(workspace: WorkspaceRef, params: ExecuteFileOpRequest): Promise<FileOpResponse> {
    return this.http.post(`${cloudFilesystemPrefix}/file-ops/${await this.resolveWorkspaceId(workspace)}/execute`, params);
  }

  async readFile(workspace: WorkspaceRef, path: string): Promise<FileReadResult> {
    const response = await this.executeFileOp(workspace, {
      op_type: 'read',
      path,
    });
    return parseFileReadResponse(path, response);
  }

  async writeFile(
    workspace: WorkspaceRef,
    path: string,
    content: string,
    expectedVersion?: string
  ): Promise<FileWriteResult> {
    const response = await this.executeFileOp(workspace, {
      op_type: 'write',
      path,
      content,
      ...(expectedVersion !== undefined ? { expected_version: expectedVersion } : {}),
    });
    if (response.status === 'conflict') {
      throw new FileWriteConflictError(
        path,
        response.current_version ?? null,
        response.current_content ?? undefined
      );
    }
    if (response.status === 'error' || response.error) {
      throw new Error(`writeFile(${path}) failed: ${response.error ?? 'unknown error'}`);
    }
    if (!response.version) {
      throw new Error(`writeFile(${path}) returned no version token`);
    }
    return { version: response.version };
  }

  async viewChanges(workspace: WorkspaceRef, sessionId: string): Promise<VersioningOpResponse> {
    return this.http.get(`${cloudFilesystemPrefix}/file-ops/${await this.resolveWorkspaceId(workspace)}/changes`, {
      session_id: sessionId,
    });
  }

  readonly subscribeEvents = async (
    workspace: WorkspaceRef,
    params: SubscribeWorkspaceEventsRequest
  ): Promise<void> => {
    return this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/state/subscribe`, params);
  };

  readonly unsubscribeEvents = async (workspace: WorkspaceRef, deviceId: string): Promise<void> => {
    return this.http.deleteWithBody(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/state/subscribe`, {
      device_id: deviceId,
    });
  };

  async saveVersion(
    workspace: WorkspaceRef,
    params: SaveCheckpointRequest
  ): Promise<VersioningOpResponse> {
    return this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/versions`, params);
  }

  async listHistory(
    workspace: WorkspaceRef,
    params: ViewHistoryParams
  ): Promise<VersionHistoryResponse> {
    return this.http.get(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/versions`, params);
  }

  async restoreVersion(
    workspace: WorkspaceRef,
    params: RestoreVersionRequest
  ): Promise<VersioningOpResponse> {
    return this.http.post(`/v1/workspaces/${await this.resolveWorkspaceId(workspace)}/versions/restore`, params);
  }
}
